import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { cellToLatLng } from "h3-js";
import {
  CalendarDays,
  ChevronDown,
  CircleHelp,
  Clock3,
  LocateFixed,
  MapPin,
  MoonStar,
  SlidersHorizontal,
  ShoppingBasket,
  WashingMachine,
  Store,
  Utensils,
  X,
  Drama,
  ExternalLink,
} from "lucide-react";
import {
  aggregateCells,
  cellsToGeoJson,
  coverageCells,
  filterVenuesByCategory,
  openVenuesToGeoJson,
} from "../domain/aggregation.js";
import { SLOT_MINUTES, slotForDate, stateAt } from "../domain/schedule.js";
import {
  venueCategories,
  type CellSummary,
  type CityIndexEntry,
  type CityManifest,
  type Venue,
  type VenueCategory,
  type VenueState,
} from "../domain/types.js";
import { closestCity } from "../domain/cities.js";
import { loadCity, loadCityIndex, type LoadedCity } from "../data/loadCity.js";
import { MapView } from "../map/MapView.js";
import { queryStringForState, readRoutedState, type RoutedState } from "./queryState.js";

const categoryMeta: Record<
  VenueCategory,
  { label: string; icon: typeof Utensils; className: string }
> = {
  food: { label: "Food + drink", icon: Utensils, className: "food" },
  groceries: { label: "Groceries", icon: ShoppingBasket, className: "groceries" },
  retail: { label: "Retail", icon: Store, className: "retail" },
  services: { label: "Services", icon: WashingMachine, className: "services" },
  entertainment: { label: "Entertainment", icon: Drama, className: "entertainment" },
};

const stateLabels: Record<VenueState, string> = {
  open: "Definitely open",
  closed: "Definitely closed",
  uncertain: "Conditional / uncertain",
  missing: "Hours not mapped",
  invalid: "Hours unsupported",
};

const noVenues: Venue[] = [];

function roundedNow(timezone: string): DateTime {
  const now = DateTime.now().setZone(timezone);
  return now.set({
    minute: Math.floor(now.minute / SLOT_MINUTES) * SLOT_MINUTES,
    second: 0,
    millisecond: 0,
  });
}

function selectedDateFor(manifest: CityManifest, route?: RoutedState): DateTime {
  const windowStart = DateTime.fromISO(manifest.window.start);
  const windowEnd = DateTime.fromISO(manifest.window.end);
  if (route?.date && route.time) {
    const routedDate = DateTime.fromISO(`${route.date}T${route.time}`, {
      zone: manifest.city.timezone,
    });
    if (
      routedDate.isValid &&
      routedDate.minute % SLOT_MINUTES === 0 &&
      routedDate >= windowStart &&
      routedDate < windowEnd
    ) {
      return routedDate;
    }
  }

  const now = roundedNow(manifest.city.timezone);
  return now >= windowStart && now < windowEnd ? now : windowStart.plus({ hours: 20 });
}

function formatTime(date: DateTime, manifest: CityManifest): string {
  return date.setLocale(manifest.city.locale).toFormat("ccc d LLL · h:mm a");
}

function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 6_000,
      maximumAge: 300_000,
    });
  });
}

function TimeControls({
  selected,
  manifest,
  onChange,
  onNow,
  locating,
  locationError,
}: {
  selected: DateTime;
  manifest: CityManifest;
  onChange: (date: DateTime) => void;
  onNow: () => void;
  locating: boolean;
  locationError?: string;
}) {
  const minutes = selected.hour * 60 + selected.minute;
  const startDate = DateTime.fromISO(manifest.window.start).toISODate()!;
  const endDate = DateTime.fromISO(manifest.window.end).minus({ days: 1 }).toISODate()!;
  return (
    <div className="time-controls">
      <div className="time-heading">
        <div>
          <span className="eyebrow">Local time in {manifest.city.displayName}</span>
          <strong>{formatTime(selected, manifest)}</strong>
        </div>
        <button type="button" className="now-button" onClick={onNow} disabled={locating}>
          <LocateFixed size={14} /> {locating ? "Locating…" : "Now"}
        </button>
      </div>
      {locationError && <p className="location-error">{locationError}</p>}
      <div className="date-time-row">
        <label className="date-input">
          <CalendarDays size={15} />
          <input
            type="date"
            min={startDate}
            max={endDate}
            value={selected.toISODate()!}
            onChange={(event) => {
              const date = DateTime.fromISO(event.target.value, { zone: manifest.city.timezone });
              onChange(date.set({ hour: selected.hour, minute: selected.minute }));
            }}
            aria-label="Selected date"
          />
        </label>
        <span className="time-pill">
          <Clock3 size={14} />
          {selected.toFormat("h:mm a")}
        </span>
      </div>
      <input
        className="time-slider"
        type="range"
        min={0}
        max={24 * 60 - SLOT_MINUTES}
        step={SLOT_MINUTES}
        value={minutes}
        onChange={(event) => {
          const next = Number(event.target.value);
          onChange(selected.set({ hour: Math.floor(next / 60), minute: next % 60 }));
        }}
        aria-label="Selected time in fifteen-minute increments"
      />
      <div className="time-scale">
        <span>12 am</span>
        <span>Noon</span>
        <span>11:45 pm</span>
      </div>
    </div>
  );
}

function CategoryFilters({
  selected,
  onToggle,
}: {
  selected: ReadonlySet<VenueCategory>;
  onToggle: (category: VenueCategory) => void;
}) {
  return (
    <div className="category-filters" aria-label="Venue categories">
      {venueCategories.map((category) => {
        const meta = categoryMeta[category];
        const Icon = meta.icon;
        return (
          <button
            key={category}
            type="button"
            className={`category-button ${meta.className} ${selected.has(category) ? "active" : ""}`}
            onClick={() => onToggle(category)}
            aria-pressed={selected.has(category)}
          >
            <Icon size={16} />
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}

function CellPanel({ cell, onClose }: { cell: CellSummary; onClose: () => void }) {
  const knownPercent = cell.total ? Math.round((cell.known / cell.total) * 100) : 0;
  const closed = cell.known - cell.open;
  const listedOpen = Math.min(cell.open, cell.openVenues.length);
  const [latitude, longitude] = cellToLatLng(cell.h3);
  return (
    <aside className="inspector" aria-label="Selected map cell">
      <button className="close-button" type="button" onClick={onClose} aria-label="Close inspector">
        <X size={18} />
      </button>
      <span className="eyebrow">Map cell · {cell.h3.toUpperCase()}</span>
      <span className="cell-location">
        Centre {latitude.toFixed(4)}, {longitude.toFixed(4)}
      </span>
      <h2>{cell.open} definitely open</h2>
      <p className="cell-explanation">
        Of {cell.total} mapped venues, <strong>{cell.open}</strong> {cell.open === 1 ? "is" : "are"}{" "}
        confirmed open, <strong>{closed}</strong> {closed === 1 ? "is" : "are"} confirmed closed,
        and <strong>{cell.possible}</strong> {cell.possible === 1 ? "has" : "have"} missing,
        unsupported, or uncertain hours.
      </p>
      <div className="metric-grid">
        <div>
          <strong>{cell.possible}</strong>
          <span>hours unknown</span>
        </div>
        <div>
          <strong>{cell.missingHours}</strong>
          <span>missing OSM hours</span>
        </div>
        <div>
          <strong>{cell.total}</strong>
          <span>mapped venues</span>
        </div>
        <div>
          <strong>{knownPercent}%</strong>
          <span>confirmed state</span>
        </div>
      </div>
      <h3>All mapped venues by category</h3>
      <div className="breakdown">
        {venueCategories
          .filter((category) => cell.categories[category] > 0)
          .map((category) => (
            <div key={category}>
              <span className={`legend-dot ${categoryMeta[category].className}`} />
              <span>{categoryMeta[category].label}</span>
              <strong>{cell.categories[category]} mapped</strong>
            </div>
          ))}
      </div>
      <h3>
        Definitely open here ·{" "}
        {cell.open > listedOpen ? `showing ${listedOpen} of ${cell.open}` : cell.open}
      </h3>
      {cell.openVenues.length ? (
        <ul className="open-list">
          {cell.openVenues.map((venue) => (
            <li key={venue.id}>
              <strong>{venue.name}</strong>
              <span>{venue.subtype.replace("=", " · ")}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No venue is definitely open in this cell at the selected time.</p>
      )}
    </aside>
  );
}

function VenuePanel({ venue, slot, onClose }: { venue: Venue; slot: number; onClose: () => void }) {
  const state = stateAt(venue.schedule, slot);
  return (
    <aside className="inspector venue-inspector" aria-label="Selected venue">
      <button className="close-button" type="button" onClick={onClose} aria-label="Close inspector">
        <X size={18} />
      </button>
      <span className={`state-badge ${state}`}>{stateLabels[state]}</span>
      <h2>{venue.name}</h2>
      <p className="venue-type">
        {categoryMeta[venue.category].label} · {venue.subtype.replace("=", " / ")}
      </p>
      <dl className="venue-details">
        {venue.schedule.effectiveExpression && (
          <>
            <dt>
              {venue.schedule.source === "official_website" ? "Website hours" : "Mapped hours"}
            </dt>
            <dd>{venue.schedule.effectiveExpression}</dd>
          </>
        )}
        {venue.schedule.source === "official_website" && venue.websiteHours && (
          <>
            <dt>Schedule source</dt>
            <dd>
              Structured opening-hours data extracted from the official website as a backfill;
              OpenStreetMap has no mapped hours for this place.
            </dd>
          </>
        )}
        {venue.schedule.sourceField === "opening_hours:kitchen" && (
          <>
            <dt>Schedule used</dt>
            <dd>Kitchen opening hours</dd>
          </>
        )}
        {venue.address && (
          <>
            <dt>Address</dt>
            <dd>{venue.address}</dd>
          </>
        )}
      </dl>
      <div className="venue-links">
        {venue.website && (
          <a href={venue.website} target="_blank" rel="noreferrer">
            Website <ExternalLink size={14} />
          </a>
        )}
        <a href={venue.osmUrl} target="_blank" rel="noreferrer">
          OpenStreetMap source <ExternalLink size={14} />
        </a>
      </div>
    </aside>
  );
}

export function App() {
  const [initialRoute] = useState(() => readRoutedState(window.location.search));
  const [data, setData] = useState<LoadedCity | null>(null);
  const [cityIndex, setCityIndex] = useState<CityIndexEntry[]>([]);
  const [error, setError] = useState<string>();
  const [loadingCity, setLoadingCity] = useState(false);
  const [selectedDate, setSelectedDate] = useState<DateTime>();
  const [categories, setCategories] = useState<Set<VenueCategory>>(() => initialRoute.categories);
  const [selectedCellH3, setSelectedCellH3] = useState<string | undefined>(
    initialRoute.selectedCellH3,
  );
  const [selectedVenueId, setSelectedVenueId] = useState<string | undefined>(
    initialRoute.selectedVenueId,
  );
  const [legendOpen, setLegendOpen] = useState(initialRoute.legendOpen);
  const [controlsOpen, setControlsOpen] = useState(initialRoute.controlsOpen);
  const [showCoverage, setShowCoverage] = useState(initialRoute.showCoverage);
  const [userLocation, setUserLocation] = useState<[number, number]>();
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string>();

  useEffect(() => {
    loadCityIndex()
      .then(async (cities) => {
        setCityIndex(cities);
        const routedCity = cities.find((city) => city.id === initialRoute.cityId);
        let initialCityId = routedCity?.id ?? "brisbane";
        if (!routedCity && window.isSecureContext && navigator.geolocation) {
          setLocating(true);
          try {
            const position = await getCurrentPosition();
            const coordinates: [number, number] = [
              position.coords.longitude,
              position.coords.latitude,
            ];
            setUserLocation(coordinates);
            initialCityId = closestCity(coordinates, cities)?.id ?? initialCityId;
          } catch {
            // Location is optional; Brisbane remains the deterministic fallback.
          } finally {
            setLocating(false);
          }
        }
        const loaded = await loadCity(initialCityId, cities);
        setData(loaded);
        setSelectedDate(selectedDateFor(loaded.manifest, initialRoute));
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      );
  }, [initialRoute]);

  const slot = data && selectedDate ? slotForDate(selectedDate, data.manifest.window) : 0;
  const cityVenues = data?.venues ?? noVenues;
  // Cells, points, and summary metrics intentionally share one eligible set so a
  // category change can never leave coverage representing hidden categories.
  const visible = useMemo(
    () => filterVenuesByCategory(cityVenues, categories),
    [cityVenues, categories],
  );
  const cells = useMemo(() => aggregateCells(visible, slot), [visible, slot]);
  const cellGeoJson = useMemo(() => cellsToGeoJson(cells), [cells]);
  const currentCoverageCells = useMemo(() => coverageCells(cells), [cells]);
  const coverageCellGeoJson = useMemo(
    () => cellsToGeoJson(currentCoverageCells),
    [currentCoverageCells],
  );
  const venueGeoJson = useMemo(() => openVenuesToGeoJson(visible, slot), [visible, slot]);
  const selectedCell = useMemo(() => {
    const eligibleCells = showCoverage ? currentCoverageCells : cells;
    return eligibleCells.find((cell) => cell.h3 === selectedCellH3);
  }, [cells, currentCoverageCells, selectedCellH3, showCoverage]);
  const selectedVenue = useMemo(
    () => visible.find((venue) => venue.id === selectedVenueId),
    [selectedVenueId, visible],
  );
  const openCount = useMemo(
    () => visible.filter((venue) => stateAt(venue.schedule, slot) === "open").length,
    [visible, slot],
  );
  const knownCount = useMemo(
    () =>
      visible.filter((venue) => {
        const state = stateAt(venue.schedule, slot);
        return state === "open" || state === "closed";
      }).length,
    [visible, slot],
  );
  const coverage = visible.length ? Math.round((knownCount / visible.length) * 100) : 0;

  function toggleCategory(category: VenueCategory) {
    setCategories((current) => {
      const next = new Set(current);
      if (next.has(category) && next.size > 1) next.delete(category);
      else next.add(category);
      return next;
    });
    setSelectedCellH3(undefined);
    setSelectedVenueId(undefined);
  }

  function goToNow() {
    if (!data) return;
    setSelectedDate(roundedNow(data.manifest.city.timezone));
    setLocationError(undefined);

    if (!window.isSecureContext) {
      setLocationError("Location needs a secure HTTPS connection on this device.");
      return;
    }
    if (!navigator.geolocation) {
      setLocationError("This browser does not provide location access.");
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([position.coords.longitude, position.coords.latitude]);
        setLocating(false);
      },
      (geolocationError) => {
        const message =
          geolocationError.code === geolocationError.PERMISSION_DENIED
            ? "Location access was not allowed."
            : geolocationError.code === geolocationError.TIMEOUT
              ? "Finding your location timed out. Tap Now to retry."
              : "Your location is currently unavailable.";
        setLocationError(message);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 30_000 },
    );
  }

  async function changeCity(cityId: string) {
    if (cityId === data?.manifest.city.id) return;
    setLoadingCity(true);
    setError(undefined);
    try {
      const loaded = await loadCity(cityId, cityIndex);
      setData(loaded);
      setSelectedDate(selectedDateFor(loaded.manifest));
      setSelectedCellH3(undefined);
      setSelectedVenueId(undefined);
      setLocationError(undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setLoadingCity(false);
    }
  }

  useEffect(() => {
    if (!data || !selectedDate) return;
    const query = queryStringForState(window.location.search, {
      cityId: data.manifest.city.id,
      date: selectedDate.toISODate()!,
      time: selectedDate.toFormat("HH:mm"),
      categories,
      showCoverage,
      legendOpen,
      controlsOpen,
      selectedCellH3,
      selectedVenueId,
    });
    const nextUrl = `${window.location.pathname}${query}${window.location.hash}`;
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== nextUrl) {
      window.history.replaceState(window.history.state, "", nextUrl);
    }
  }, [
    categories,
    controlsOpen,
    data,
    legendOpen,
    selectedCellH3,
    selectedDate,
    selectedVenueId,
    showCoverage,
  ]);

  if (error) {
    return (
      <main className="status-screen">
        <MoonStar />
        <h1>The city data did not wake up.</h1>
        <p>{error}</p>
        <code>pnpm data:fixture</code>
      </main>
    );
  }
  if (!data || !selectedDate) {
    return (
      <main className="status-screen loading">
        <MoonStar />
        <p>Listening for the city after dark…</p>
      </main>
    );
  }

  const generated = DateTime.fromISO(data.manifest.generatedAt)
    .setLocale(data.manifest.city.locale)
    .toLocaleString(DateTime.DATE_MED);
  const missingOsmPercent = data.manifest.quality.relevantVenueCount
    ? Math.round(
        (data.manifest.quality.missingScheduleCount / data.manifest.quality.relevantVenueCount) *
          100,
      )
    : 0;
  const websiteHoursEligibleCount = data.manifest.quality.websiteHoursEligibleCount ?? 0;
  const websiteHoursEligiblePercent = data.manifest.quality.missingScheduleCount
    ? Math.round((websiteHoursEligibleCount / data.manifest.quality.missingScheduleCount) * 100)
    : 0;

  return (
    <main className={`app-shell ${controlsOpen ? "" : "controls-collapsed"}`}>
      <MapView
        key={data.manifest.city.id}
        manifest={data.manifest}
        cellGeoJson={cellGeoJson}
        coverageCellGeoJson={coverageCellGeoJson}
        venueGeoJson={venueGeoJson}
        cellSummaries={cells}
        venues={visible}
        showCoverage={showCoverage}
        userLocation={userLocation}
        onSelectCell={(cell) => {
          setSelectedCellH3(cell.h3);
          setSelectedVenueId(undefined);
        }}
        onSelectVenue={(venue) => {
          setSelectedVenueId(venue.id);
          setSelectedCellH3(undefined);
        }}
      />

      <header className="brand">
        <div className="brand-mark">
          <MoonStar size={19} />
        </div>
        <div>
          <h1>Vampire Maps</h1>
          <span>{data.manifest.city.displayName} after dark</span>
        </div>
      </header>

      <section className={`control-panel ${controlsOpen ? "" : "controls-collapsed"}`}>
        <div className="city-select" aria-label="Selected city">
          <div>
            <MapPin size={15} />
            <select
              value={data.manifest.city.id}
              onChange={(event) => void changeCity(event.target.value)}
              disabled={loadingCity}
              aria-label="Choose city"
            >
              {cityIndex.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.displayName}
                </option>
              ))}
            </select>
          </div>
          <ChevronDown className="city-chevron" size={14} />
          <button
            type="button"
            className="controls-toggle"
            onClick={() => setControlsOpen((value) => !value)}
            aria-expanded={controlsOpen}
            aria-controls="map-controls"
          >
            <SlidersHorizontal size={16} />
            {controlsOpen ? "Hide controls" : "Show controls"}
            <ChevronDown size={15} />
          </button>
        </div>
        <div id="map-controls" className="map-controls-body">
          <TimeControls
            selected={selectedDate}
            manifest={data.manifest}
            onChange={setSelectedDate}
            onNow={goToNow}
            locating={locating}
            locationError={locationError}
          />
          <CategoryFilters selected={categories} onToggle={toggleCategory} />
        </div>
      </section>

      <section className="summary-card" aria-live="polite">
        <div className="pulse-dot" />
        <p>
          <strong>
            {openCount.toLocaleString()} {openCount === 1 ? "venue" : "venues"}
          </strong>{" "}
          {openCount === 1 ? "is" : "are"} definitely open.
          <span> Usable hours cover {coverage}% of relevant mapped venues.</span>
        </p>
      </section>

      <section className={`legend-card ${legendOpen ? "expanded" : ""}`}>
        <button
          type="button"
          onClick={() => setLegendOpen((value) => !value)}
          aria-expanded={legendOpen}
        >
          <span>
            <CircleHelp size={15} /> How to read this map
          </span>
          <ChevronDown size={14} />
        </button>
        <div className="legend-content">
          <div>
            <span className="swatch open" /> Brighter areas have more definitely open venues
          </div>
          <div>
            <span className="swatch unknown" /> Gold outlines flag weaker hours coverage
          </div>
          <div>
            <span className="swatch point" /> Points appear as you zoom in
          </div>
          <button
            type="button"
            className={`coverage-button ${showCoverage ? "active" : ""}`}
            onClick={() => setShowCoverage((value) => !value)}
            aria-pressed={showCoverage}
          >
            {showCoverage ? "Hide" : "Show"} OSM hours coverage
          </button>
          {showCoverage && (
            <div className="coverage-scale">
              <span>More missing</span>
              <i />
              <span>More mapped</span>
            </div>
          )}
          <p>Unknown hours are not treated as closed.</p>
        </div>
      </section>

      <footer className="data-note">
        <span>Data generated {generated}</span>
        <span>
          {data.manifest.quality.parsedScheduleCount.toLocaleString()} of{" "}
          {data.manifest.quality.relevantVenueCount.toLocaleString()} schedules usable
        </span>
        <span>
          {data.manifest.quality.missingScheduleCount.toLocaleString()} missing OSM hours (
          {missingOsmPercent}%)
          {data.manifest.quality.websiteHoursBackfillCount
            ? ` · ${data.manifest.quality.websiteHoursBackfillCount.toLocaleString()} website backfills`
            : ""}
        </span>
        <span>
          {websiteHoursEligibleCount.toLocaleString()} missing-hours places have an official website
          ({websiteHoursEligiblePercent}% of missing)
        </span>
        <span>Mapped hours, not foot traffic or safety</span>
      </footer>

      {selectedCell && (
        <CellPanel cell={selectedCell} onClose={() => setSelectedCellH3(undefined)} />
      )}
      {selectedVenue && (
        <VenuePanel
          venue={selectedVenue}
          slot={slot}
          onClose={() => setSelectedVenueId(undefined)}
        />
      )}
    </main>
  );
}
